import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import * as xml2js from 'xml2js';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CIKService {
  // PROXY_HERO = 'https://cors-anywhere.herokuapp.com/';
  PROXY_HERO = environment.proxy_herokuapp;
  PROXY_LOCAL = environment.proxy_local;
  PROXY_ALL_ORIGINS = environment.proxy_all_origins;
  CORS_MODE: RequestMode = environment.cors_mode as RequestMode;
  MAX_FILINGS = 15;
  cikSubject$ = new Subject();
  prevCIK: string;
  currCIK: string;
  items;
  // lcp --proxyUrl https://www.sec.gov/Archives/edgar/data

  getCurrentCIK() {
    return this.currCIK;
  }

  getCIK(cik) {
    this.currCIK = cik;
    // if same cik requested, return saved items
    if (this.currCIK === this.prevCIK) {
      setTimeout(() => {
        this.cikSubject$.next({ cik: this.currCIK, items: this.items });
      }, 350);
    }
    const RSS_URL = `https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=` +
      cik + `&type=4%25&dateb=&owner=only&start=0&count=40&output=atom`;
    const url = this.PROXY_HERO + RSS_URL;

    // tslint:disable-next-line: object-literal-key-quotes
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw Error(response.statusText);
        }
        return response.text();
      })
      .then(data => {
        // console.log('cik data:', data);
        return this.parseRSS(data);
      })
      .then((rssEntries: any) => {
        return this.getFilings(rssEntries);
      })
      .then((filings) => {
        return this.parseFilings(filings);
      })
      .then((parsed: any) => {
        parsed.forEach(tr => {

          if (!tr.isDirector) {
            tr.isDirector = '0';
          }
          if (!tr.isTenPercentOwner) {
            tr.isTenPercentOwner = '0';
          }
          if (!tr.isOfficer) {
            tr.isOfficer = '0';
          }
          if (!tr.officerTitle) {
            tr.officerTitle = '';
          }
          tr.isDirector = this.pipeRole(tr.isDirector);
          tr.isTenPercentOwner = this.pipeRole(tr.isTenPercentOwner);
          tr.isOfficer = this.pipeRole(tr.isOfficer);
        });
        this.items = [...parsed];
        this.prevCIK = cik;
        // prevent sending two results when user clicks different rows quickly
        if (this.items[0].CIK === this.currCIK) {
          this.cikSubject$.next({ cik: this.currCIK, items: parsed });
        }
      })
      .catch(error => {
        this.cikSubject$.next(error.message);
        return console.warn('There has been a problem with getting the sec.gov rss feed:', error);
      });
  };

  parseRSS(data) {
    return new Promise(resolve => {
      let k;
      const arr = [];
      const parser = new xml2js.Parser(
        {
          trim: true,
          explicitArray: true
        });
      parser.parseString(data, (err, result) => {
        if (err) {
          return console.error('error parsing rss', err);
        }
        const obj = result.feed;
        // tslint:disable-next-line: forin
        for (k in obj.entry) {
          const item = obj.entry[k];
          const linkHtm = item.link[0].$.href;
          const link = linkHtm.replace('-index.htm', '.txt');
          const updated = item.updated[0];
          const idNode = item.id[0];
          const startIndex = idNode.lastIndexOf('=');
          const id = idNode.substr(startIndex + 1);

          arr.push({
            title: item.title[0],
            id,
            linkHtm,
            link,
            updated
          });
        }
        resolve(arr);
      });
    });
  }

  parseFilings(entries) {
    return new Promise(resolve => {
      const newTrans = [];
      entries.forEach((item, i) => {
        const startXML = item.details.indexOf('<ownershipDocument>');
        const endXML = item.details.indexOf('</ownershipDocument>');
        const details = item.details.substr(startXML, endXML);
        const parser = new xml2js.Parser(
        );

        parser.parseString(details, (err, result) => {
          if (err) {
            console.log('err: ', err);
          }

          try {
            const obj = result.ownershipDocument;
            const nonDerivativeTransactions = obj.nonDerivativeTable[0].nonDerivativeTransaction;
            nonDerivativeTransactions.forEach((transaction) => {
              // TRANSACTIONS
              // tslint:disable-next-line: prefer-const
              let tr: Transaction = new Transaction();
              tr.transactionDate = transaction.transactionDate[0].value[0];
              tr.transactionCode = transaction.transactionCoding[0].transactionCode[0];
              tr.transactionShares = transaction.transactionAmounts[0].transactionShares[0].value[0];
              tr.transactionPricePerShare = transaction.transactionAmounts[0].transactionPricePerShare[0].value[0];
              tr.sharesOwnedFollowingTransaction = transaction.postTransactionAmounts[0].sharesOwnedFollowingTransaction[0].value[0];
              const transactionValue = Math.floor(+tr.transactionPricePerShare * +tr.transactionShares);
              tr.transactionValue = transactionValue;

              // SYMBOL
              tr.symbol = obj.issuer[0].issuerTradingSymbol[0];

              // REPORTING OWNER
              tr.reportingOwner = obj.reportingOwner[0].reportingOwnerId[0].rptOwnerName[0];
              if (obj.reportingOwner[0].reportingOwnerRelationship[0].isDirector) {
                tr.isDirector = obj.reportingOwner[0].reportingOwnerRelationship[0].isDirector[0];
              }
              if (obj.reportingOwner[0].reportingOwnerRelationship[0].isTenPercentOwner) {
                tr.isTenPercentOwner = obj.reportingOwner[0].reportingOwnerRelationship[0].isTenPercentOwner[0];
              }
              if (obj.reportingOwner[0].reportingOwnerRelationship[0].isOfficer) {
                tr.isOfficer = obj.reportingOwner[0].reportingOwnerRelationship[0].isOfficer[0];
              }
              if (obj.reportingOwner[0].reportingOwnerRelationship[0].officerTitle) {
                tr.officerTitle = obj.reportingOwner[0].reportingOwnerRelationship[0].officerTitle[0];
              }
              tr.CIK = obj.issuer[0].issuerCik[0];
              // PROPS FROM RSS FEED ENTRY
              tr.linkHtm = entries[i].linkHtm;
              tr.updated = entries[i].updated;
              if (
                (tr.transactionCode === 'P' || tr.transactionCode === 'S') &&
                tr.symbol !== 'NONE' &&
                tr.symbol !== 'N/A' &&
                tr.symbol[0] !== '?'
              ) {
                newTrans.push(tr);
              }
            });

          } catch (err) {
            // return console.error('error in parsing filing, properties missing, do not use', entries[i]);
            return null;
          }
        });
      });
      resolve(newTrans);
    });
  }

  timeout(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async getFilings(entries) {
    const numOfFilings = entries.length > this.MAX_FILINGS ? this.MAX_FILINGS : entries.length;
    // console.log('numOfFilings: ', numOfFilings);
    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < numOfFilings; i++) {
      const XML = entries[i].link;
      // handle calls one by one and wait with returning result until all are done
      const partialXML = XML.slice(XML.indexOf('data/') + 4);
      await fetch(this.PROXY_ALL_ORIGINS + XML)
        .then(response => response.text())
        .then(data => {
          entries[i].details = data;
          return null;
        })
        .catch(error => {
          return console.error('There has been a problem with getting details:', error);
        });
      // no more than ten requests per second
      await this.timeout(10);
    }
    return entries.slice(0, numOfFilings);
  }

  getTimestamp(str) {
    const ts = Date.parse(str);
    return ts / 1000;
  }

  pipeRole(val) {
    switch (val) {
      case 'false':
      case '':
      case undefined:
        val = '0';
        break;
      case 'true':
        val = '1';
        break;
      default:
        break;
    }
    return val;
  }

}

export class Transaction {
  company: string;
  CIK: string;
  linkHtm: string;
  updated: string;
  ts: number;
  transactionDate: string;
  transactionCode: string;
  transactionShares: string;
  transactionPricePerShare: string;
  transactionAcquiredDisposedCode: string;
  sharesOwnedFollowingTransaction: string;
  transactionValue: number;
  relations: string;
  symbol: string;
  reportingOwner: string;
  isDirector: string;
  isTenPercentOwner: string;
  isOfficer: string;
  officerTitle: string;
}